"use client";

import type { SplitTreatments } from "@zapier/bff";
import { useCurrentAccountId } from "@zapier/identity";
import { trpc, type RouterOutputs } from "../trpc";
import { useEffect, useState } from "react";

type SplitCheckOutput = RouterOutputs["split"]["check"];
type WhatsNewAnnouncementData = NonNullable<
  NonNullable<SplitCheckOutput>["whats_new_popover_enabled"]
>;
export type ShowWhatsNewAnnouncementConfig = NonNullable<
  WhatsNewAnnouncementData["config"]
>;

export function useSplitTreatments() {
  const [isLoaded, setIsLoaded] = useState(false);

  const accountId = useCurrentAccountId();
  const resp = trpc.split.check.useQuery(
    {
      accountId,
      featureFlags: [
        "manage_projects_mvp",
        "editor_speculation_rules_assetmanagement",
        "ee_mpe__agents_list_page_in_assetmanagement",
        "ee_assetmanagement__project_sunset",
        "generate_ai_notes_debug_mode_enabled",
        "assetmanagement_duplicate_assets",
        "assetmanagement_managed_connections_filter",
        "assetmanagement_global_search_enabled",
        "assetmanagement_approval_diff_viewer",
        "assetmanagement_last_run",
        "assetmanagement_zap_health_dashboard",
        "whats_new_popover_enabled",
        "bulk_move_assets",
        "copy_table_records",
        "connection_usage_history_enabled",
        "editor_zap-templates-edit-mode",
        "assetmanagement_duplicate_template",
        "sdk-credentials-enabled",
        "assetmanagement_zap_and_canvas_unified_trash_enabled",
        "assetmanagement_trash_index_enabled",
        "governance_inbox_release",
        "governance_inbox_v1_transport",
        "apcon_convert_connection_to_managed",
        "assetmanagement_super_admin_personal_folders",
        "assetmanagement_bulk_change_owner",
        "assetmanagement_ngz_bulk_change_owner",
        "assetmanagement_ngz_bulk_move",
        "assetmanagement_ngz_bulk_trash",
        "assetmanagement_ngz_bulk_copy",
        "forms_access",
      ] satisfies SplitTreatments[],
    },
    {
      staleTime: 1000 * 60 * 10,
    },
  );

  useEffect(() => {
    if (!isLoaded && resp.isSuccess) {
      setIsLoaded(true);
    }
  }, [resp.isSuccess, isLoaded]);

  const showWhatsNewPopoverRaw = resp.data?.whats_new_popover_enabled;
  const showWhatsNewPopoverTreatment =
    (showWhatsNewPopoverRaw as { treatment?: string } | undefined)?.treatment ??
    "off";
  const showWhatsNewAnnouncementConfig:
    | ShowWhatsNewAnnouncementConfig
    | undefined =
    typeof showWhatsNewPopoverRaw === "object" &&
    showWhatsNewPopoverRaw !== null
      ? (showWhatsNewPopoverRaw as { config?: ShowWhatsNewAnnouncementConfig })
          .config
      : undefined;

  const flags = {
    agentsListPageInAssetManagement:
      resp.data?.ee_mpe__agents_list_page_in_assetmanagement,
    manageProjectsMvp: resp.data?.manage_projects_mvp,
    editorSpeculationRulesAssetManagement:
      resp.data?.editor_speculation_rules_assetmanagement,
    projectSunset: resp.data?.ee_assetmanagement__project_sunset,
    generateAiNotesDebugModeEnabled:
      resp.data?.generate_ai_notes_debug_mode_enabled,
    duplicateAssetsEnabled: resp.data?.assetmanagement_duplicate_assets,
    managedConnectionsFilterEnabled:
      resp.data?.assetmanagement_managed_connections_filter,
    globalSearchEnabled: resp.data?.assetmanagement_global_search_enabled,
    approvalDiffViewer: resp.data?.assetmanagement_approval_diff_viewer,
    bulkMoveAssets: resp.data?.bulk_move_assets,
    lastRunEnabled: resp.data?.assetmanagement_last_run,
    zapHealthDashboard: resp.data?.assetmanagement_zap_health_dashboard,
    copyTableRecords: resp.data?.copy_table_records,
    connectionUsageHistoryEnabled: resp.data?.connection_usage_history_enabled,
    editorZapTemplatesEditMode: resp.data?.["editor_zap-templates-edit-mode"],
    duplicateTemplateEnabled: resp.data?.assetmanagement_duplicate_template,
    sdkCredentialsEnabled: resp.data?.["sdk-credentials-enabled"],
    zapAndCanvasUnifiedTrashEnabled:
      resp.data?.assetmanagement_zap_and_canvas_unified_trash_enabled,
    trashIndexEnabled: resp.data?.assetmanagement_trash_index_enabled,
    governanceInboxRelease: resp.data?.governance_inbox_release,
    governanceInboxV1Transport: resp.data?.governance_inbox_v1_transport,
    convertConnectionToManagedEnabled:
      resp.data?.apcon_convert_connection_to_managed,
    superAdminPersonalFoldersEnabled:
      resp.data?.assetmanagement_super_admin_personal_folders,
    bulkChangeOwnerEnabled: resp.data?.assetmanagement_bulk_change_owner,
    ngzBulkChangeOwner: resp.data?.assetmanagement_ngz_bulk_change_owner,
    ngzBulkMove: resp.data?.assetmanagement_ngz_bulk_move,
    ngzBulkTrash: resp.data?.assetmanagement_ngz_bulk_trash,
    ngzBulkCopy: resp.data?.assetmanagement_ngz_bulk_copy,
    nextGenFormsEnabled: resp.data?.forms_access,
  };

  return {
    flags,
    showWhatsNewAnnouncementConfig,
    isShowWhatsNewAnnouncementEnabled: showWhatsNewPopoverTreatment === "on",
    isAgentsListPageInAssetManagement:
      flags.agentsListPageInAssetManagement === "on",
    isProjectsMVPEnabled: flags.manageProjectsMvp === "on",
    editorSpeculationRules:
      flags.editorSpeculationRulesAssetManagement || "off",
    isProjectSunsetEnabled: flags.projectSunset === "on",
    isGenerateAiNotesDebugModeEnabled:
      flags.generateAiNotesDebugModeEnabled === "on",
    isDuplicateAssetsEnabled: flags.duplicateAssetsEnabled === "on",
    isManagedConnectionsFilterEnabled:
      flags.managedConnectionsFilterEnabled === "on",
    isGlobalSearchEnabled: flags.globalSearchEnabled === "on",
    isApprovalDiffViewerEnabled: flags.approvalDiffViewer === "on",
    isBulkMoveAssetsEnabled: flags.bulkMoveAssets === "on",
    isLastRunEnabled: flags.lastRunEnabled === "on",
    isZapHealthDashboardEnabled: flags.zapHealthDashboard === "on",
    isCopyTableRecordsEnabled: flags.copyTableRecords === "on",
    isConnectionUsageHistoryEnabled:
      flags.connectionUsageHistoryEnabled === "on",
    isEditorZapTemplatesEditModeEnabled:
      flags.editorZapTemplatesEditMode === "on",
    isDuplicateTemplateEnabled: flags.duplicateTemplateEnabled === "on",
    isSdkCredentialsEnabled: flags.sdkCredentialsEnabled === "on",
    isZapAndCanvasUnifiedTrashEnabled:
      flags.zapAndCanvasUnifiedTrashEnabled === "on",
    isTrashIndexEnabled: flags.trashIndexEnabled === "on",
    isGovernanceInboxReleased: flags.governanceInboxRelease === "on",
    isGovernanceInboxV1TransportEnabled:
      flags.governanceInboxV1Transport === "on",
    isConvertConnectionToManagedEnabled:
      flags.convertConnectionToManagedEnabled === "on",
    isSuperAdminPersonalFoldersEnabled:
      flags.superAdminPersonalFoldersEnabled === "on",
    isBulkChangeOwnerEnabled: flags.bulkChangeOwnerEnabled === "on",
    isNgzBulkChangeOwnerEnabled: flags.ngzBulkChangeOwner === "on",
    isNgzBulkMoveEnabled: flags.ngzBulkMove === "on",
    isNgzBulkTrashEnabled: flags.ngzBulkTrash === "on",
    isNgzBulkCopyEnabled: flags.ngzBulkCopy === "on",
    isNextGenFormsEnabled: flags.nextGenFormsEnabled === "on",
    isLoading: resp.isLoading,
    isLoaded,
    isError: resp.isError,
  };
}
